import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {PageEvent} from '@angular/material/paginator';
import {MatSort, Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {AltProperties, ManagedSubscriber} from '@nh/angular-tses';
import {AccessibilityKeysService, DasAccessibilityItem} from 'app/Accessibility/accessibility-keys.service';
import {SearchColumn} from 'app/search/query/enums/search-column';
import {SearchEntry} from 'app/search/query/enums/search-result';
import {SEARCHABLE_COLUMN_METADATA} from 'app/search/query/enums/searchable-column-metadata';
import {PaginationAndSortData, SearchService} from 'app/search/search.service';
import {DashboardQuery} from '../services/dashboard.service';
import {Template} from '../templatiser/templatiser.component';

@Component({
  selector: 'das-departure-index',
  templateUrl: './departure-index.component.html',
  styleUrls: ['./departure-index.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DepartureIndexComponent extends ManagedSubscriber implements OnInit {
  private static readonly DEFAULT_MESSAGE = 'No results.';
  public static readonly DEFAULT_PAGINATION_AND_SORT: Readonly<PaginationAndSortData> = {
    pageIndex: 0,
    pageSize: 25,
    columnSelected: SearchColumn.DEPARTURE_ID_NUMBER_AND_REVISION,
    direction: 'asc'
  };
  private static readonly ASCII_A = 97;
  private static readonly MAX_ALT_ACCESSIBILITY_KEYS = 25;
  public readonly DasAccessibilityItem: typeof DasAccessibilityItem = DasAccessibilityItem;

  protected noResultMessageLookup: Record<DashboardQuery, string> = {
    [DashboardQuery.WAITING_ON_ME]: 'No departures awaiting your input.',
    [DashboardQuery.IM_INVOLVED_IN]: 'No departures in which you are involved.',
    [DashboardQuery.MY_ORGANISATIONS]: 'You are not part of an organisation with any departures.',
    [DashboardQuery.MY_DISCIPLINES]: 'You are not part of a discipline.',
    [DashboardQuery.WITHDRAWN_BEFORE_SUBMISSION]: 'No results.',
    [DashboardQuery.POST_DETERMINATION]: 'No results.'
  };

  public dataSource: MatTableDataSource<SearchEntry> = new MatTableDataSource();

  public pageIndex: number;
  public pageSize: number;
  public columnSelected: string;
  public direction: string;

  public emptyMessage: string;

  @Input() public set entries(entries: SearchEntry[]) {
    this.dataSource = new MatTableDataSource(entries);
    this._entries = entries;
  }

  public get entries(): SearchEntry[] {
    return this._entries;
  }

  private _entries: SearchEntry[] = [];

  public get hasDisplayedResults(): boolean {
    return !!this.entries ? this.entries.length > 0 : false;
  }
  @Input() public totalNumberOfResults: number = null;

  @Input() public set displayedColumns(cols: SearchColumn[]) {
    const filteredColumns = new Set([SearchColumn.REVISION, SearchColumn.DEPARTURE_ID_NUMBER]);
    this._displayedColumns = cols.filter(col => !filteredColumns.has(col));
  }

  public get displayedColumns() {
    return this._displayedColumns;
  }

  private _displayedColumns: SearchColumn[] = [];

  @Output() public entrySelected = new EventEmitter<SearchEntry>();

  @Output() public tableChange = new EventEmitter<PaginationAndSortData>();

  @ViewChild(MatSort, {static: true}) public sort: MatSort;

  constructor(private searchService: SearchService, public accessibilityKeysService: AccessibilityKeysService) {
    super();
  }

  public ngOnInit(): void {
    this.paginationAndSortDefaults();
    this.subscribeTo(this.searchService.getEmptyMessageSubscriber(), (message: string) => {
      this.emptyMessage = this.noResultMessageLookup[message]
        ? this.noResultMessageLookup[message]
        : DepartureIndexComponent.DEFAULT_MESSAGE;
    });
  }

  public onPaginationChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginationAndSort();
  }

  public onSortChange(event: Sort) {
    this.direction = event.direction || null;
    this.columnSelected = this.direction ? event.active : null;
    this.resetPageIndex();
    this.updatePaginationAndSort();
  }

  private updatePaginationAndSort() {
    const paginationAndSortData: PaginationAndSortData = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      columnSelected: this.columnSelected,
      direction: this.direction
    };
    this.resetScrollBar();
    this.tableChange.emit(paginationAndSortData);
  }

  public onEntryClick(result: SearchEntry) {
    this.entrySelected.emit(result);
  }

  public noEntries(): boolean {
    return !this.entries || this.entries.length < 1;
  }

  public resetPageIndex(): void {
    this.pageIndex = 0;
    this.resetScrollBar();
  }

  private paginationAndSortDefaults(): void {
    this.pageIndex = DepartureIndexComponent.DEFAULT_PAGINATION_AND_SORT.pageIndex;
    this.pageSize = DepartureIndexComponent.DEFAULT_PAGINATION_AND_SORT.pageSize;
    this.columnSelected = DepartureIndexComponent.DEFAULT_PAGINATION_AND_SORT.columnSelected;
    this.direction = DepartureIndexComponent.DEFAULT_PAGINATION_AND_SORT.direction;
  }

  private resetScrollBar() {
    const table = document.querySelector('.ts-scroll-top');
    table.scrollTop = 0;
  }

  public getMinWidth(col: string): string {
    return SEARCHABLE_COLUMN_METADATA[col] && SEARCHABLE_COLUMN_METADATA[col].minWidth
      ? SEARCHABLE_COLUMN_METADATA[col].minWidth
      : 'inherit';
  }

  public getColumnHeader(displayedColumn: SearchColumn): string {
    return SEARCHABLE_COLUMN_METADATA[displayedColumn].displayName;
  }

  public getColumnValue(displayedColumn: SearchColumn, result: SearchEntry): Template {
    return SEARCHABLE_COLUMN_METADATA[displayedColumn].displayFunction(result);
  }

  public getAltProperties(entry: SearchEntry): AltProperties {
    const entryIndex = this._entries.findIndex(e => e[SearchColumn.DEPARTURE_ID] === entry[SearchColumn.DEPARTURE_ID]);
    const parentProp = this.accessibilityKeysService.getAltProperties(DasAccessibilityItem.DEPARTURE_LIST);
    return new AltProperties(
      parentProp.initialKey,
      String.fromCharCode(DepartureIndexComponent.ASCII_A + entryIndex),
      parentProp.condition
    );
  }

  public shouldAddAltAccessibilityKey(entry: SearchEntry, columnIndex: number) {
    const entryIndex: number = this._getEntryIndex(entry);
    return (
      this._isFinalColumn(columnIndex) &&
      entryIndex !== -1 &&
      entryIndex < DepartureIndexComponent.MAX_ALT_ACCESSIBILITY_KEYS
    );
  }

  private _isFinalColumn(index: number): boolean {
    return index === this._displayedColumns.length - 1;
  }

  private _getEntryIndex(entry: SearchEntry): number {
    return this._entries.findIndex(e => e[SearchColumn.DEPARTURE_ID] === entry[SearchColumn.DEPARTURE_ID]);
  }
}
